import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

export default function Hero({
  title = "Experience the Best Beach Hotels in Dubai",
  subtitle = "Discover luxury, comfort and world-class hospitality at the most beautiful beach hotels in Dubai. Your perfect getaway awaits.",
  backgroundImage,
  backgroundImages = [],
  variant = "inner",
  overlayOpacity = "bg-black/10",
  children,
  slideInterval = 5000,
}) {
  const isHome = variant === "home";

  const [current, setCurrent] = useState(0);

  // =========================================================
  // AUTO SLIDER
  // =========================================================

  useEffect(() => {
    if (!isHome || backgroundImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setCurrent((prev) => {
        return (prev + 1) % backgroundImages.length;
      });
    }, slideInterval);

    return () => {
      clearInterval(interval);
    };
  }, [isHome, backgroundImages, slideInterval]);

  // =========================================================
  // ACTIVE BACKGROUND IMAGE
  // =========================================================

  const activeImage =
    backgroundImages.length > 0
      ? backgroundImages[current]
      : backgroundImage;

  // =========================================================
  // HOME HERO
  // =========================================================

  if (isHome) {
    return (
      <section
        className="
          relative
          -mt-0
          w-full
          overflow-visible
          bg-[#F3F8FF]
        "
      >
        {/* =====================================================
            HERO IMAGE AREA
        ===================================================== */}

        <div
          className="
            relative
            h-[620px]
            w-full
            overflow-hidden

            sm:h-[650px]

            md:h-[670px]

            lg:h-[700px]

            xl:h-[720px]
          "
        >
          {/* =================================================
              BACKGROUND SLIDER
          ================================================= */}

          {backgroundImages.length > 0 ? (
            <>
              {backgroundImages.map((image, index) => (
                <div
                  key={index}
                  className="
                    absolute
                    inset-0
                    bg-cover
                    bg-center
                    bg-no-repeat
                    transition-opacity
                    duration-1000
                    ease-in-out
                  "
                  style={{
                    backgroundImage: `url(${image})`,
                    opacity: index === current ? 1 : 0,
                  }}
                />
              ))}
            </>
          ) : (
            activeImage && (
              <div
                className="
                  absolute
                  inset-0
                  bg-cover
                  bg-center
                  bg-no-repeat
                "
                style={{
                  backgroundImage: `url(${activeImage})`,
                }}
              />
            )
          )}

          {/* =================================================
              MAIN DARK GRADIENT
          ================================================= */}

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-r
              from-[#062F3A]/85
              via-[#073E48]/55
              via-[55%]
              to-transparent
            "
          />

          {/* =================================================
              TOP GRADIENT
              Helps white header navigation remain visible.
          ================================================= */}

          <div
            className="
              pointer-events-none
              absolute
              inset-x-0
              top-0
              h-[180px]
              bg-gradient-to-b
              from-[#8B0000]/65
              to-transparent
            "
          />

          {/* =================================================
              BOTTOM GRADIENT
          ================================================= */}

          <div
            className="
              pointer-events-none
              absolute
              inset-x-0
              bottom-0
              h-[220px]
              bg-gradient-to-t
              from-[#062F3A]/35
              via-transparent
              to-transparent
            "
          />

          {/* =================================================
              HERO CONTENT
          ================================================= */}

          <div
            className="
    relative
    z-10
    mx-auto
    flex
    h-full
    w-full
    max-w-[1440px]
    items-center
    justify-between

    px-5

    pb-[90px]
    pt-[110px]

    sm:px-8
    sm:pb-[105px]
    sm:pt-[120px]

    lg:px-12
    lg:pb-[120px]
    lg:pt-[125px]

    xl:px-16
  "
          >
            <div
              className="
                max-w-[680px]
                text-left
              "
            >
              {/* =================================================
                  EYEBROW
              ================================================= */}

              <div
                className="
                  mb-5
                  flex
                  items-center
                  gap-3

                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[0.22em]
                  text-white/95

                  sm:text-[10px]

                  md:text-[11px]
                "
              >
                <span
                  className="
                    h-[1px]
                    w-8
                    bg-[#8B0000]
                  "
                />

                <span>
                  Luxury Stays
                </span>

                <span className="text-[#8B0000]">
                  •
                </span>

                <span>
                  Breathtaking Views
                </span>

                <span className="text-[#8B0000]">
                  •
                </span>

                <span>
                  Unforgettable Experiences
                </span>
              </div>

              {/* =================================================
                  HEADING
              ================================================= */}

              <h1
                className="
    max-w-[600px]
    font-serif
    text-[32px]
    font-medium
    leading-[1.08]
    tracking-[-0.03em]
    text-white

    drop-shadow-[0_4px_18px_rgba(0,0,0,0.30)]

    sm:text-[38px]

    md:text-[44px]

    lg:text-[52px]

    xl:text-[58px]
  "
              >
                {formatHeroTitle(title)}
              </h1>

              {/* =================================================
                  SUBTITLE
              ================================================= */}

              {subtitle && (
                <p
                  className="
                    mt-6
                    max-w-[560px]

                    text-[13px]
                    font-normal
                    leading-6
                    text-white/90

                    sm:text-[14px]
                    sm:leading-6

                    md:text-[15px]
                    md:leading-7

                    lg:text-[16px]
                  "
                >
                  {subtitle}
                </p>
              )}

              {/* =================================================
                  CTA BUTTONS
              ================================================= */}

              <div
                className="
                  mt-7
                  flex
                  flex-wrap
                  items-center
                  gap-3
                "
              >
                {/* PRIMARY CTA */}

                <NavLink
                  to="/hotels"
                  className="
                    group
                    inline-flex
                    items-center
                    justify-center
                    gap-3
                    rounded-full
                    bg-[#8B0000]
                    px-6
                    py-3.5
                    text-[12px]
                    font-semibold
                    text-white
                    hover:text-[#173D3D]

                    shadow-[0_8px_25px_rgba(0,0,0,0.18)]

                    transition-all
                    duration-300

                    hover:-translate-y-0.5
                    hover:bg-[#FFD54F]
                    hover:shadow-[0_12px_30px_rgba(0,0,0,0.24)]

                    sm:px-7
                    sm:py-3.5
                    sm:text-[13px]
                  "
                >
                  <span>
                    Explore Hotels
                  </span>

                  <span
                    className="
                      text-[16px]
                      transition-transform
                      duration-300
                      group-hover:translate-x-1
                    "
                  >
                    →
                  </span>
                </NavLink>
              </div>
            </div>
            {/* =================================================
    ATTENTION DEAL CARD
================================================= */}

            <div
              className="
    hidden
    lg:block
    relative
    w-[330px]
    xl:w-[370px]
    shrink-0
    mr-4
    xl:mr-8
  "
            >
              <div
                className="
      relative
      overflow-hidden
      rounded-[28px]
      border
      border-white/30
      bg-white/95
      p-6
      shadow-[0_25px_70px_rgba(0,0,0,0.28)]
      backdrop-blur-xl
    "
              >
                {/* Decorative red glow */}

                <div
                  className="
        absolute
        -right-16
        -top-16
        h-40
        w-40
        rounded-full
        bg-[#8B0000]/10
        blur-2xl
      "
                />

                {/* Top Badge */}

                <div
                  className="
        relative
        mb-5
        flex
        items-center
        justify-between
      "
                >
                  <span
                    className="
          rounded-full
          bg-[#8B0000]
          px-3
          py-1.5
          text-[9px]
          font-bold
          uppercase
          tracking-[0.16em]
          text-white
        "
                  >
                    Exclusive Deal
                  </span>

                  <span
                    className="
          text-xl
        "
                  >
                    ✦
                  </span>
                </div>

                {/* Main Text */}

                <div className="relative">
                  <p
                    className="
          text-[11px]
          font-semibold
          uppercase
          tracking-[0.18em]
          text-[#8B0000]
        "
                  >
                    Dubai Hotel Collection
                  </p>

                  <h3
                    className="
          mt-2
          font-serif
          text-[30px]
          font-medium
          leading-tight
          tracking-[-0.025em]
          text-[#123F42]
        "
                  >
                    Stay More.
                    <br />
                    <span className="italic text-[#8B0000]">
                      Save More.
                    </span>
                  </h3>

                  <p
                    className="
          mt-3
          max-w-[270px]
          text-[12px]
          leading-5
          text-[#64747A]
        "
                  >
                    Discover handpicked beach hotels and luxury stays
                    across Dubai at exclusive rates.
                  </p>
                </div>

                {/* Discount */}

                <div
                  className="
        relative
        mt-5
        flex
        items-center
        gap-4
        rounded-2xl
        bg-[#F8F5EF]
        px-4
        py-3
      "
                >
                  <div
                    className="
          flex
          h-12
          w-12
          shrink-0
          items-center
          justify-center
          rounded-full
          bg-[#8B0000]
          text-center
          text-white
        "
                  >
                    <div>
                      <div className="text-[15px] font-bold leading-none">
                        60%
                      </div>

                      <div className="mt-0.5 text-[7px] uppercase tracking-wider">
                        OFF
                      </div>
                    </div>
                  </div>

                  <div>
                    <p
                      className="
            text-[11px]
            font-bold
            text-[#123F42]
          "
                    >
                      Special Hotel Rates
                    </p>

                    <p
                      className="
            mt-0.5
            text-[10px]
            text-[#71808A]
          "
                    >
                      Limited-time offers
                    </p>
                  </div>
                </div>

                {/* CTA */}

                <NavLink
                  to="/hotels"
                  className="
        group
        relative
        mt-5
        flex
        w-full
        items-center
        justify-between
        rounded-full
        bg-[#123F42]
        px-5
        py-3.5
        text-[11px]
        font-semibold
        text-white
        transition-all
        duration-300
        hover:-translate-y-0.5
        hover:bg-[#8B0000]
        hover:shadow-[0_10px_25px_rgba(139,0,0,0.22)]
      "
                >
                  <span>
                    Explore Dubai Hotels
                  </span>

                  <span
                    className="
          text-base
          transition-transform
          duration-300
          group-hover:translate-x-1
        "
                  >
                    →
                  </span>
                </NavLink>

                {/* Trust line */}

                <div
                  className="
        relative
        mt-4
        flex
        items-center
        justify-center
        gap-2
        text-[9px]
        text-[#7A898E]
      "
                >
                  <span className="text-[#8B0000]">✓</span>

                  <span>
                    Best rates • Secure booking • Trusted stays
                  </span>
                </div>
              </div>
            </div>


          </div>

          {/* =================================================
              SLIDER DOTS
          ================================================= */}

          {backgroundImages.length > 1 && (
            <div
              className="
                absolute
                bottom-[105px]
                left-1/2
                z-20
                flex
                -translate-x-1/2
                items-center
                gap-2

                sm:bottom-[115px]

                lg:bottom-[125px]
              "
            >
              {backgroundImages.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => setCurrent(index)}
                  className={`
                    h-1.5
                    rounded-full
                    transition-all
                    duration-300

                    ${index === current
                      ? "w-8 bg-[#FFD54F]"
                      : "w-1.5 bg-white/60 hover:bg-white"
                    }
                  `}
                />
              ))}
            </div>
          )}
        </div>

        {/* =====================================================
            SEARCH PANEL

            Overlaps the bottom of the hero just like
            the Beach Hotels Dubai homepage design.
        ===================================================== */}

        {children && (
          <div
            className="
    relative
    z-30
    mx-auto
    -mt-[67px]
    w-[calc(100%-24px)]
    max-w-[1320px]

    sm:-mt-[67px]
    sm:w-[calc(100%-40px)]

    md:w-[calc(100%-64px)]

    lg:-mt-[67px]
    lg:w-[calc(100%-96px)]

    xl:w-[calc(100%-128px)]
  "
          >
            <div
              className="
                rounded-[22px]
                border
                border-[#E4E9ED]
                bg-white
                p-2

                shadow-[0_18px_50px_rgba(7,53,64,0.18)]

                sm:rounded-[24px]
                sm:p-2.5

                lg:rounded-[26px]
                lg:p-3
              "
            >
              {children}
            </div>
          </div>
        )}



        
      </section>
    );
  }

  // ===========================================================
  // INNER PAGE HERO
  // ===========================================================

  return (
    <section
      className="
        relative
        mt-[76px]
        w-full
        overflow-hidden
        bg-[#F3F8FF]
      "
    >
      {/* =====================================================
          BACKGROUND
      ===================================================== */}

      {activeImage && (
        <div
          className="
            absolute
            inset-0
            bg-cover
            bg-center
            bg-no-repeat
          "
          style={{
            backgroundImage: `url(${activeImage})`,
          }}
        />
      )}

      {/* =====================================================
          INNER PAGE OVERLAY
      ===================================================== */}

      <div
        className="
          absolute
          inset-0
          bg-gradient-to-r
          from-[#063D47]/80
          via-[#063D47]/60
          to-[#063D47]/45
        "
      />

      {/* =====================================================
          INNER PAGE CONTENT
      ===================================================== */}

      <div
        className="
          relative
          z-10
          mx-auto
          flex
          min-h-[300px]
          w-full
          max-w-[1440px]
          items-center
          justify-center
          px-5
          py-16
          text-center

          sm:min-h-[340px]
          sm:px-8

          md:min-h-[380px]

          lg:px-12
        "
      >
        <div
          className="
            mx-auto
            max-w-4xl
            text-white
          "
        >
          {/* =================================================
              INNER EYEBROW
          ================================================= */}

          <div
            className="
              mb-5
              inline-flex
              items-center
              gap-3
              text-[10px]
              font-semibold
              uppercase
              tracking-[0.20em]
              text-white/90
            "
          >
            <span
              className="
                h-[1px]
                w-8
                bg-[#8B0000]
              "
            />

            Beach Hotels Dubai
          </div>

          {/* =================================================
              INNER TITLE
          ================================================= */}

          <h1
            className="
              font-serif
              text-[38px]
              font-medium
              leading-tight
              tracking-[-0.025em]
              text-white
              drop-shadow-[0_4px_15px_rgba(0,0,0,0.25)]

              sm:text-[46px]

              md:text-[54px]

              lg:text-[60px]
            "
          >
            {formatHeroTitle(title)}
          </h1>

          {/* =================================================
              INNER SUBTITLE
          ================================================= */}

          {subtitle && (
            <p
              className="
                mx-auto
                mt-5
                max-w-2xl
                text-sm
                leading-6
                text-white/90

                md:text-base
                md:leading-7
              "
            >
              {subtitle}
            </p>
          )}

          {/* =================================================
              ADDITIONAL CONTENT
          ================================================= */}

          {children && (
            <div className="mt-7">
              {children}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}


// ===========================================================
// HERO TITLE FORMATTER
// ===========================================================
//
// Makes:
//
// "Experience the Best Beach Hotels in Dubai"
//
// into:
//
// Experience the Best Beach Hotels in
// Dubai
//
// with Dubai highlighted in gold.
//
// ===========================================================

function formatHeroTitle(title) {
  if (!title) {
    return null;
  }

  const normalizedTitle = title.toLowerCase();

  if (normalizedTitle.includes("dubai")) {
    const match = title.match(
      /^(.*?)(\s+in\s+)(dubai)(.*)$/i
    );

    if (match) {
      const before = match[1];
      const after = match[4];

      return (
        <>
          {before}
          <br />

          <span
            className="
              italic
             text-[#D7A74A]
            "
          >
            Dubai
          </span>

          {after}
        </>
      );
    }

    const parts = title.split(/(dubai)/i);

    return parts.map((part, index) => {
      if (part.toLowerCase() === "dubai") {
        return (
          <span
            key={index}
            className="
              italic
              text-[#8B0000]
            "
          >
            {part}
          </span>
        );
      }

      return (
        <React.Fragment key={index}>
          {part}
        </React.Fragment>
      );
    });
  }

  return title;
}